/**
 * The export-type tag shown on every Exported row ("✂ Supercut", "GIF"...).
 * `kind` is the export task type the backend recorded; old rows have none, so we fall back
 * to the file extension.
 */
const KIND_LABELS: Record<string, string> = {
  mp4: "Video",
  mp4_super_cut: "✂ Supercut",
  gif: "GIF",
  mp3: "MP3",
  audiogram: "Audio + logo",
  srt: "SRT",
  vtt: "VTT",
  zip_srt: "Subtitles ZIP",
  stitch_creations: "Stitched",
};

const KIND_COLORS: Record<string, string> = {
  mp4: "#3b82f6",
  mp4_super_cut: "#e53935",
  gif: "#a855f7",
  mp3: "#10b981",
  audiogram: "#14b8a6",
  srt: "#f59e0b",
  vtt: "#f59e0b",
  zip_srt: "#f59e0b",
  stitch_creations: "#7c3aed",
};

export const exportKindKey = (kind?: string, ext?: string): string => {
  const k = (kind || "").toLowerCase();
  if (k && KIND_LABELS[k]) return k;
  const e = (ext || "").toLowerCase();
  if (e === "zip") return "zip_srt";
  if (e === "mp4" || e === "mov" || e === "webm") return "mp4";
  return KIND_LABELS[e] ? e : "";
};

export const exportKindLabel = (kind?: string, ext?: string): string =>
  KIND_LABELS[exportKindKey(kind, ext)] || (ext || "").toUpperCase();

const SHAPE_LABELS: Record<string, string> = {
  original: "Original",
  landscape: "Wide 16:9",
  vertical: "Vertical · Blur",
  vertical_fill: "Vertical · Zoom",
};
const CAPTION_LABELS: Record<string, string> = {
  word: "one-word captions",
  group: "word-group captions",
  sentence: "sentence captions",
};

/** How an export was made, next to its tag: "Vertical · Blur · one-word captions", and for a
 *  Super Cut "6 words · no breathing room · same length off · Original". Old rows: "". */
export const exportSettingsLabel = (
  kind?: string,
  settings?: { pad_ms?: number; equalize?: boolean; words?: number; shape?: string; captions?: string; framed?: number; caption_moved?: boolean },
): string => {
  if (!settings) return "";
  const parts: string[] = [];
  if ((kind || "").toLowerCase() === "mp4_super_cut" && (settings.pad_ms !== undefined || settings.words)) {
    // Both handles every time, on or off, so the row always tells how it was cut.
    if (settings.words) parts.push(`${settings.words} word${settings.words === 1 ? "" : "s"}`);
    parts.push(settings.pad_ms ? `+${settings.pad_ms} ms breathing room` : "no breathing room");
    parts.push(settings.equalize ? "same length on" : "same length off");
  }
  if (settings.shape) parts.push(SHAPE_LABELS[settings.shape] || settings.shape);
  if (settings.captions) parts.push(CAPTION_LABELS[settings.captions] || "captions");
  if (settings.caption_moved) parts.push("captions moved");
  if (settings.framed) parts.push(`${settings.framed} clip${settings.framed === 1 ? "" : "s"} framed`);
  return parts.join(" · ");
};

export const exportKindColor = (kind?: string, ext?: string): string =>
  KIND_COLORS[exportKindKey(kind, ext)] || "#555";
